import React, {
  //useRef,
  useContext,
  useEffect,
  useState,
} from "react";
import AuthContext from "contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { getQueryParams } from "lib/utils";
import { Trans, useTranslation } from "react-i18next";

import "material-symbols/outlined.css";
import "./style.css";

import { Header } from "components/header";
import { Footer } from "components/footer/footer";
import { Button } from "components/button";
import Modal from "components/modal";
import { AnimatePresence } from "framer-motion";

//assets

// import BgEstimateEuro from "assets/landingEstimateEuro.png";
import BgLandingHero from "assets/landingHero.png";
// import BgLandingHeroEuro from "assets/landingHeroEuro.png";
import { TradeInIcon } from "pages/svgs/tradein-logo";
import useIsMobile from "hooks/useIsMobile";

import useWhen from "hooks/useWhen";
import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";

import {
  EVENT_7,
  EVENT_18,
  EVENT_21,
  EVENT_22,
  trackingMap,
} from "utils/tracking";
import { END_OF_PILOT_25_ARGS } from "consts/feature-config";

const track = (name) => {
  const resolvedName = trackingMap[name];

  console.log(`Tracked: "${name}" as "${resolvedName}"`);

  if (window.s) {
    window.s.events = resolvedName;
    window.s.linkTrackEvents = resolvedName;
    window.s.linkTrackVars = "events";
    window.s.tl(null, "o", name);
  }
};

const REPLAY_URL = "https://www.lego.com/en-us/sustainability/replay";

const Landing = () => {
  const [takeBackTypeToggle] = useState("mail");
  const [previousTakeBackTypeToggle, setPreviousTakeBackTypeToggle] =
    useState(`mail`);
  const [showReplayModal, setShowReplayModal] = useState(false);

  // Close the Replay modal on escape
  useEffect(() => {
    if (!showReplayModal) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowReplayModal(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showReplayModal]);

  useEffect(() => {
    if (takeBackTypeToggle !== previousTakeBackTypeToggle) {
      setPreviousTakeBackTypeToggle(takeBackTypeToggle);
      track(takeBackTypeToggle === `store` ? EVENT_21 : EVENT_22);
    }
  }, [previousTakeBackTypeToggle, takeBackTypeToggle]);

  // translation
  const { t } = useTranslation();
  // const { i18n } = useTranslation();

  // auth
  const { user } = useContext(AuthContext);

  // Get query params
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  // feature flag for end of pilot revised landing page
  // const useRevisedLandingPage = useWhen(END_OF_PILOT_ARGS);

  const CtaButton = ({ className, additionalTracking = [] }) => {
    // If isQuickReg is true (boolean), use the store-signup/0-landing.jsx logic
    if (queryParams.isQuickReg) {
      return (
        <Button
          tracking={[EVENT_7, ...additionalTracking]}
          href={`${t("urls.my_trade_ins")}`}
          queryParams={queryParams}
          className={className}
        >
          {!user && "Sign In and "}View Your Trade-Ins
        </Button>
      );
    }
    // Default behavior
    return (
      <Button
        data-testid="default-start-trade-in"
        tracking={[EVENT_7, ...additionalTracking]}
        href={`${t("urls.my_trade_ins")}`}
        queryParams={{
          ...queryParams,
        }}
        className={className}
      >
        {!user && "Sign In and "}View Your Trade-Ins
      </Button>
    );
  };

  const AccordionTrigger = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Header className="AccordionHeader text-primary text-2xl">
        <Accordion.Trigger
          className={classNames("AccordionTrigger text-left", className)}
          {...props}
          ref={forwardedRef}
        >
          <div>{children}</div>
          <span
            className="AccordionChevron material-symbols-outlined align-bottom ml-large text-secondary"
            aria-hidden
          >
            keyboard_arrow_down
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
    )
  );

  const AccordionContent = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Content
        className={classNames("AccordionContent", className)}
        {...props}
        ref={forwardedRef}
      >
        <div className="AccordionContentText text-secondary mt-large mb-regular">
          {children}
        </div>
      </Accordion.Content>
    )
  );

  const hideStartTradeIn = useWhen(END_OF_PILOT_25_ARGS);

  const isMobile = useIsMobile();
  const navigationLink = `${t("urls.my_trade_ins")}/${location.search}`;

  const mobileContent = (
    <>
      <Link to={navigationLink}>
        <TradeInIcon />
      </Link>
    </>
  );

  return (
    <div>
      <Header mobileChildren={mobileContent} classNames="bg-yellow border-none">
        <div className="flex gap-large">
          {!hideStartTradeIn && (
            <CtaButton
              className="hidden lg:block"
              additionalTracking={[EVENT_18]}
            />
          )}

          {!isMobile && (
            <Button
              href={user ? `${t("urls.my_trade_ins")}` : `${t("urls.login")}`}
              variant="secondary_no_border"
              queryParams={queryParams}
            >
              {user
                ? t("landing.button_my_trade_ins")
                : t("landing.button_sign_in")}
            </Button>
          )}
        </div>
      </Header>
      <div className="bg-[#ffef87] h-[71px] mt-[-71px]"></div>
      <div
        id="hero"
        className="bg-gradient-to-b from-[#FFF087] via-[#FFE950] to-[#FFE21B] px-x-large md:px-xxxx-large relative"
      >
        <div className="bg-white/50 p-small text-center text-xs absolute bottom-0 left-0 right-0 z-20">
          <Trans
            i18nKey={"landing.pentatonic"}
            components={{
              1: (
                <Link
                  to={t("urls.terms_and_conditions")}
                  className="cursor-pointer text-blue"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </div>
        <div className="container mx-auto flex flex-col md:flex-row md:items-end gap-xx-large max-w-full md:max-w-6xl relative z-10 pb-xxx-large md:pb-0">
          <div className="mx-auto md:mx-0 mt-xx-large md:mt-[4rem] max-w-[48rem] mb-xx-large md:mb-[200px] lg:mb-xxxx-large">
            <h1 className="text-primary font-bold text-[3rem] leading-[1.2] md:text-[3.5rem] lg:text-[4rem] mb-large">
              The LEGO® Brick Take Back pilot ended on October 19th 2025.
            </h1>
            <p className="text-secondary text-2xl mb-xx-large pr-0 md:pr-56 lg:pr-0 max-w-[44rem]">
              Have bricks that you’d like to pass on? Donate your pre-loved LEGO
              bricks through LEGO Replay to help kids in need. Please keep in
              mind that LEGO Replay is a donation initiative, so you won’t be
              compensated for your donation. Would you like to be redirected to
              LEGO Replay?
            </p>
            <div className="flex flex-col items-stretch md:items-center md:flex-row space-y-large md:space-y-0 md:space-x-x-large max-w-screen min-w-0">
              <Button onClick={() => setShowReplayModal(true)}>
                Visit LEGO Replay
              </Button>
            </div>
          </div>
        </div>
        <img
          src={BgLandingHero}
          alt={t("landing.hero.image_alt")}
          className="hidden md:block md:absolute md:right-0 md:bottom-0"
        />
      </div>
      <div id="faqs" className="pt-xxx-large md:pt-xxxx-large">
        <div className="flex flex-col justify-center pb-xxx-large md:pb-xxxx-large px-x-large md:px-xx-large items-center">
          <h2 className="text-primary text-[30px] md:text-[40px] leading-[39px] md:leading-[52px] font-bold text-center mb-xxx-large">
            FAQ
          </h2>
          <div className="mx-auto w-full lg:max-w-6xl mb-xx-large">
            <Accordion.Root type="single" defaultValue="item-1" collapsible>
              <Accordion.Item
                className="p-large border-b border-gray-300"
                value="item-1"
              >
                <AccordionTrigger className="flex justify-between w-full">
                  If I created a trade-in before October 19th 2025, can I still
                  send in my LEGO bricks?
                </AccordionTrigger>
                <AccordionContent>
                  Thanks for your interest in the LEGO Brick Take Back program.
                  We can no longer accept LEGO bricks. This pilot program has
                  now ended, and we are focused on gathering insights and
                  learnings from its operations.
                </AccordionContent>
              </Accordion.Item>
              <Accordion.Item
                className="p-large border-b border-gray-300"
                value="item-2"
              >
                <AccordionTrigger className="flex justify-between w-full">
                  Where can I use my trade-in gift card?
                </AccordionTrigger>
                <AccordionContent>
                  <p>LEGO® e-Gift Cards can be redeemed:</p>
                  <ul className="list-disc pl-x-large my-large">
                    <li>
                      In all{" "}
                      <a
                        href="https://www.lego.com/en-us/stores"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LEGO stores
                      </a>{" "}
                      in the U.S.
                    </li>
                    <li>
                      On{" "}
                      <a
                        href="https://www.lego.com/en-us"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LEGO.com
                      </a>
                    </li>
                    <li>
                      By phone order in the same region where the gift card was
                      issued.
                    </li>
                  </ul>
                  <p className="mb-large">
                    LEGO® e-Gift Cards can’t be redeemed at LEGOLAND® Parks,
                    LEGOLAND Discovery Centers, or other retailers.
                  </p>
                  <p>
                    Got questions about your gift card? Reach out to{" "}
                    <a
                      href="https://www.lego.com/en-us/service"
                      className="cursor-pointer text-blue"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LEGO.com Customer Service
                    </a>{" "}
                    or learn more{" "}
                    <a
                      href="https://www.lego.com/en-us/service/help-topics/article/about-lego-gift-cards"
                      className="cursor-pointer text-blue"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>
                    .
                  </p>
                </AccordionContent>
              </Accordion.Item>
              <Accordion.Item
                className="p-large border-b border-gray-300"
                value="item-3"
              >
                <AccordionTrigger className="flex justify-between w-full">
                  Who can I contact with questions about my trade-in gift card?
                </AccordionTrigger>
                <AccordionContent>
                  Got questions about your gift card? Reach out to{" "}
                  <a
                    href="https://www.lego.com/en-us/service"
                    className="cursor-pointer text-blue"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LEGO.com Customer Service
                  </a>{" "}
                  or learn more{" "}
                  <a
                    href="https://www.lego.com/en-us/service/help-topics/article/about-lego-gift-cards"
                    className="cursor-pointer text-blue"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </AccordionContent>
              </Accordion.Item>
            </Accordion.Root>
          </div>
        </div>
      </div>
      <Footer />
      <AnimatePresence>
        {showReplayModal && (
          <Modal
            modalOpen={showReplayModal}
            setModalOpen={setShowReplayModal}
            data-testid="replay-redirect-modal"
          >
            <p className="text-secondary">
              You’re about to leave our site and be redirected to LEGO Replay,
              where you can donate your pre-loved bricks to help kids in need.
              Please note, donations to LEGO Replay are not compensated. Do you
              want to continue?
            </p>
            <div className="flex flex-col-reverse md:flex-row md:justify-between gap-large mt-8">
              <Button
                variant="secondary"
                onClick={() => setShowReplayModal(false)}
              >
                Stay Here
              </Button>
              <Button onClick={() => window.location.assign(REPLAY_URL)}>
                Continue to LEGO Replay
              </Button>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Landing;
